/**
 * When a read may keep asking, and when it must stop.
 *
 * The interaction audit drew this rule for writes: a poll runs while a job is
 * outstanding and **stops** when it settles, so an idle page is not asking
 * forever. It was only ever applied to the polls a press started. Six reads
 * that no press starts were left on unconditional intervals, the discovery
 * graph alone held five, two of them per expanded node, so three open nodes
 * asked roughly seventy-two times a minute for the lifetime of the tab, about
 * a bucket whose walk sealed days ago and whose answers cannot change.
 *
 * `ReconstructionRequests` already had the right shape: a functional
 * `refetchInterval` that returns `false` once nothing is outstanding. These
 * are the predicates behind it, in one place, so "outstanding" means the same
 * thing on every surface rather than being re-decided per component.
 *
 * The one judgement worth stating: `connecting` counts as outstanding. A
 * stream that has not answered yet is not a stream that has finished, and
 * treating "not yet known" as "settled" is the same error as treating an
 * unread count as zero.
 */
import type { WalkState } from '@/components/WalkProgress';

/** Whether the walk stream still has something to report on this bucket. */
export function walkOutstanding(state: WalkState | null | undefined): boolean {
  return state === 'connecting' || state === 'live';
}

/** The reconstruction request states that are still going somewhere. */
const RECONSTRUCTION_RUNNING = ['REQUESTED', 'DISPATCHED'];

/**
 * Whether any reconstruction on this bucket is still to land.
 *
 * Promotion enqueues one and a sweep drains the queue every five minutes, so
 * a records page can genuinely change with nobody pressing anything. That is
 * the one reason a settled walk is not enough to stop reading.
 */
export function reconstructionOutstanding(items: readonly { state?: string }[] | null | undefined): boolean {
  return (items ?? []).some((r) => typeof r.state === 'string' && RECONSTRUCTION_RUNNING.includes(r.state));
}

/**
 * An interval, or `false` for "stop asking".
 *
 * Named rather than written inline at each call site because the sites are
 * the problem: five of them in one file, each a bare number, and no way to
 * see from any one of them that the page as a whole never stops.
 */
export function every(ms: number, outstanding: boolean): number | false {
  return outstanding ? ms : false;
}
